import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { masterAssemblyApi } from "../../apiServices/masterassembly";
import { generatePartCatalogPdfZip } from "../../apiServices/partsCatalogApi";
import {
  createManualTranslationQueue,
  fetchManualJson,
  fetchLatestManualTranslationsByManual,
  type ManualTranslationLatestDto,
} from "../../apiServices/manualsApi";
import {
  createPartsTranslationQueue,
  fetchLatestPartsTranslationsByMasterAssembly,
  type PartsTranslationLatestDto,
} from "../../apiServices/partsTranslationsApi";
import { SHOW_MANUALS } from "../../config/features";
import { useUserStore } from "../../store/userStore";
import { useAuthoringStore } from "../../store/UseAuthorStore";
import { useManualAuthoringStore } from "../../store/manualAuthoringStore";
import { useCatalogLanguageStore } from "../../store/catalogLanguageStore";
import type { MasterAssembly } from "../../models/MasterAssembly";
import type { ManualDto } from "../../models/Manual";
import "./ViewPartnumber.css";

const ROLE_SUPERADMIN = "SUPERADMIN";
const ROLE_AUTHOR = "AUTHOR";
const ROLE_OEM = "OEM";
const ROLE_REVIEWER = "REVIEWER";
const ROLE_OEM_REVIEWER = "OEMREVIEWER";
const ROLE_VENDOR = "VENDOR";

const STATUS_PUSH_TO_TOPLEVEL = "PUSH TO TOPLEVEL";
const STATUS_REVISION_LOCKED = "REVISION LOCKED"
const CATALOG_LOCKED_STATUS_ID = 5011;
const DEFAULT_MANUAL_TRANSLATION_VENDOR = "lar1";
const DEFAULT_PARTS_TRANSLATION_VENDOR = "partsgptto";

const TRANSLATION_LANGUAGE_OPTIONS = [
  { code: "fr", label: "French (FR)" },
  { code: "es", label: "Spanish (ES)" },
  { code: "de", label: "German (DE)" },
  { code: "it", label: "Italian (IT)" },
  { code: "pt", label: "Portuguese (PT)" },
  { code: "ja", label: "Japanese (JA)" },
  { code: "ko", label: "Korean (KO)" },
  { code: "zh", label: "Chinese (ZH)" },
] as const;

type TranslationScope = "manual" | "parts";

type TranslationHistoryRow = {
  id: number;
  languageCode: string;
  latestQueueId?: number | null;
  latestQueueStatus?: string | null;
  latestQueueCreatedOn?: string | null;
  latestQueueErrorMessage?: string | null;
  latestCompletedOn?: string | null;
};

const ROLES_ALL = new Set([
  ROLE_SUPERADMIN,
  ROLE_AUTHOR,
  ROLE_OEM,
  ROLE_REVIEWER,
  ROLE_OEM_REVIEWER,
  ROLE_VENDOR,
]);
const CATALOG_EDIT_ROLES = new Set([ROLE_SUPERADMIN, ROLE_AUTHOR]);
const MANUAL_EDIT_ROLES = new Set([ROLE_SUPERADMIN, ROLE_AUTHOR]);
const MANUAL_AUTHOR_ROLES = new Set([ROLE_SUPERADMIN, ROLE_AUTHOR]);
const MANUAL_VIEW_ROLES = new Set([
  ROLE_SUPERADMIN,
  ROLE_AUTHOR,
  ROLE_OEM,
  ROLE_REVIEWER,
  ROLE_OEM_REVIEWER,
  ROLE_VENDOR,
]);

const REVIEWER_REVIEWABLE = new Set([
  "AUTHORING COMPLETE",
  "AUTHORING REVIEW",
  "AUTHORING REJECTED",
  "PUBLICATION APPROVAL",
 ]);
const OEM_REVIEWER_REVIEWABLE = new Set([
  "OEM REVIEW",
  "OEM REJECTED",
  "OEM APPROVED",
  "PUSH TO TOPLEVEL",
 ]);

const normalize = (v?: string | null) => String(v ?? "").trim().toUpperCase();

function getManualActionPermissions(role: string, status: string) {
  const roleKey = normalize(role);
  const statusKey = normalize(status);

  const canEdit = MANUAL_EDIT_ROLES.has(roleKey);
  const canAuthor = MANUAL_AUTHOR_ROLES.has(roleKey) && statusKey !== STATUS_PUSH_TO_TOPLEVEL;
  const canView = MANUAL_VIEW_ROLES.has(roleKey) && (statusKey === STATUS_PUSH_TO_TOPLEVEL || statusKey === STATUS_REVISION_LOCKED);
  const canReview =
    (roleKey === ROLE_REVIEWER && REVIEWER_REVIEWABLE.has(statusKey)) ||
    (roleKey === ROLE_OEM_REVIEWER && OEM_REVIEWER_REVIEWABLE.has(statusKey)) ||
    (roleKey === ROLE_OEM && OEM_REVIEWER_REVIEWABLE.has(statusKey));

  return { canEdit, canAuthor, canView, canReview };
}

function isManualVisibleForRole(role: string, status: string) {
  const roleKey = normalize(role);
  const statusKey = normalize(status);

  if (roleKey === ROLE_SUPERADMIN || roleKey === ROLE_AUTHOR) return true;
  if (roleKey === ROLE_VENDOR) return statusKey === STATUS_REVISION_LOCKED;
  if (roleKey === ROLE_REVIEWER) {
    return REVIEWER_REVIEWABLE.has(statusKey) || statusKey === STATUS_PUSH_TO_TOPLEVEL || statusKey === STATUS_REVISION_LOCKED;
  }
  if (roleKey === ROLE_OEM_REVIEWER || roleKey === ROLE_OEM) return OEM_REVIEWER_REVIEWABLE.has(statusKey) || statusKey === STATUS_PUSH_TO_TOPLEVEL || statusKey === STATUS_REVISION_LOCKED;
  return false;
}

const ViewPartnumber: React.FC = () => {
  const { masterAssemblyId } = useParams();
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const accessToken = useUserStore((s) => s.accessToken);
  const setPartNumber = useAuthoringStore((s) => s.setPartNumber);
  const setMasterAssembly = useAuthoringStore((s) => s.setMasterAssembly);
  const setManualTitle = useManualAuthoringStore((s) => s.setManualTitle);
  const partStatuses = useAuthoringStore((s) => s.partStatuses);
  const fetchAndSetPartStatuses = useAuthoringStore((s) => s.fetchAndSetPartStatuses);
  const selectedCatalogLanguage = useCatalogLanguageStore(
    (s) => s.selectedCatalogLanguage
  );
  const setSelectedCatalogLanguage = useCatalogLanguageStore(
    (s) => s.setSelectedCatalogLanguage
  );
  const role = normalize(String(user?.role || ROLE_OEM));

  const basePath = process.env.REACT_APP_BASE_NAME || "";
  const oemFromBase = basePath.split("/")[1] || "";
  const oemFromUser = user?.oemId ? `Oem${user.oemId}` : "";
  const oem = oemFromBase || oemFromUser || "Oem1";

  const [master, setMaster] = useState<MasterAssembly | null>(null);
  const [manuals, setManuals] = useState<ManualDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [upReviseLoading, setUpReviseLoading] = useState(false);
  const [catalogPdfLoading, setCatalogPdfLoading] = useState(false);
  const [showTranslationModal, setShowTranslationModal] = useState(false);
  const [translationScope, setTranslationScope] =
    useState<TranslationScope>("manual");
  const [translationLoading, setTranslationLoading] = useState(false);
  const [translationSubmitting, setTranslationSubmitting] = useState(false);
  const [translationPdfLoadingKey, setTranslationPdfLoadingKey] = useState<
    string | null
  >(null);
  const [translationError, setTranslationError] = useState<string | null>(null);
  const [translationRows, setTranslationRows] = useState<TranslationHistoryRow[]>(
    []
  );
  const [translationSourceManual, setTranslationSourceManual] =
    useState<ManualDto | null>(null);
  const [selectedTranslationLanguage, setSelectedTranslationLanguage] =
    useState<string>("");
  const [catalogLanguageRows, setCatalogLanguageRows] = useState<
    TranslationHistoryRow[]
  >([]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const targetId = Number(masterAssemblyId);
        if (!Number.isFinite(targetId)) {
          throw new Error("Invalid component id");
        }

        const language = selectedCatalogLanguage.trim().toLowerCase();
        const data = await masterAssemblyApi.getMasterWithManualsByRole(
          oem,
          targetId,
          role,
          language || undefined
        );
        if (!mounted) return;

        if (!data.masterAssembly?.componentPartNumber) {
          throw new Error("Component not found");
        }
        setMaster(data.masterAssembly);
        setManuals(Array.isArray(data.manuals) ? data.manuals : []);
      } catch (e: any) {
        if (mounted) setError(e?.message || "Failed to load component");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    if (masterAssemblyId) void load();
    return () => {
      mounted = false;
    };
  }, [masterAssemblyId, oem, role, selectedCatalogLanguage]);

  useEffect(() => {
    let mounted = true;
    const loadCatalogLanguageRows = async () => {
      const targetId = Number(masterAssemblyId);
      if (!Number.isFinite(targetId) || targetId <= 0) {
        setCatalogLanguageRows([]);
        return;
      }

      try {
        const rows = await fetchLatestPartsTranslationsByMasterAssembly(
          oem,
          targetId
        );
        if (!mounted) return;
        setCatalogLanguageRows(mapPartsTranslations(rows));
      } catch {
        if (mounted) setCatalogLanguageRows([]);
      }
    };

    void loadCatalogLanguageRows();
    return () => {
      mounted = false;
    };
  }, [masterAssemblyId, oem]);

  useEffect(() => {
    if (partStatuses.length > 0) return;
    const statusOem = oemFromUser || oem;
    void fetchAndSetPartStatuses(statusOem);
  }, [fetchAndSetPartStatuses, oem, oemFromUser, partStatuses.length]);

  const previewUrl = useMemo(() => {
    const imgPath = String(master?.mainSvg?.folderPath || "").trim();
    const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
    if (!imgPath) return "";
    if (/^https?:\/\//i.test(imgPath) || imgPath.includes("http://") || imgPath.includes("https://")) {
      const mediaIdx = imgPath.indexOf("/media/");
      if (mediaIdx >= 0) {
        const tail = imgPath.slice(mediaIdx + "/media/".length);
        return `${baseUrl}/media/${tail}`;
      }
      return imgPath;
    }
    if (imgPath.startsWith("/media/")) return `${baseUrl}${imgPath}`;
    if (imgPath.startsWith("media/")) return `${baseUrl}/${imgPath}`;
    return `${baseUrl}/media/${imgPath}`;
  }, [master]);

  const catalogCount = master?.catalog?.length ?? 0;
  const [page, setPage] = useState(1);
  const pageSize = 5;
  const totalPages = Math.max(1, Math.ceil(catalogCount / pageSize));
  const pagedCatalog = useMemo(
    () => (master?.catalog ?? []).slice((page - 1) * pageSize, page * pageSize),
    [master, page]
  );
  const canEditCatalog = CATALOG_EDIT_ROLES.has(role);
  const canManageCatalogTranslations = CATALOG_EDIT_ROLES.has(role);
  const isCatalogLocked = master?.workspaceAssembly?.statusId === CATALOG_LOCKED_STATUS_ID;
  const currentMasterStatus = master?.workspaceAssembly?.statusId;
  const currentMasterStatusLabel =
    currentMasterStatus == null
      ? "-"
      : partStatuses.length === 0
      ? "Loading..."
      : partStatuses.find((s) => s.statusId === currentMasterStatus)?.statusType || "-";
  const catalogLanguageOptions = useMemo(() => {
    const seen = new Set<string>();
    const options = [{ code: "", label: "English" }];

    catalogLanguageRows.forEach((row) => {
      const code = String(row.languageCode || "").trim().toLowerCase();
      if (!code || seen.has(code)) return;

      const status = normalizeTranslationStatus(row.latestQueueStatus);
      if (status !== "COMPLETED" && !row.latestCompletedOn) return;

      seen.add(code);
      options.push({
        code,
        label: getTranslationLanguageLabel(code),
      });
    });

    if (selectedCatalogLanguage && !seen.has(selectedCatalogLanguage)) {
      options.push({
        code: selectedCatalogLanguage,
        label: getTranslationLanguageLabel(selectedCatalogLanguage),
      });
    }

    return options;
  }, [catalogLanguageRows, selectedCatalogLanguage]);

  useEffect(() => {
    setPage(1);
  }, [selectedCatalogLanguage]);

  const handleUpVersion = async () => {
    if (!master?.componentPartNumber) {
      window.alert("Component details are missing.");
      return;
    }

    const requestedBy =
      user?.username?.trim() || user?.emailId?.trim() || "system";

    setUpReviseLoading(true);
    try {
      const response = await masterAssemblyApi.upRevise(oem, {
        componentPartNumber: master.componentPartNumber,
        requestedBy,
      });

      const newMasterAssemblyId = Number(
        response?.data?.newMasterAssemblyId ??
          response?.data?.updatedDto?.masterAssemblyId ??
          0
      );

      if (response?.statusCode === 200 && newMasterAssemblyId > 0) {
        window.alert(response.message || "Up-revision created successfully.");
        navigate(`/authoring/view/${newMasterAssemblyId}`);
        return;
      }

      window.alert(response?.message || "Failed to create up-revision.");
    } catch (e: any) {
      window.alert(e?.message || "Failed to create up-revision.");
    } finally {
      setUpReviseLoading(false);
    }
  };

  const handleDownloadCatalogPdf = async () => {
    if (!master?.masterAssemblyId) {
      window.alert("Master assembly id is missing.");
      return;
    }

    const language = selectedCatalogLanguage.trim().toLowerCase();
    const pdfLanguage =
      language && language !== "en" && language !== "english"
        ? language
        : undefined;

    setCatalogPdfLoading(true);
    try {
      const result = await generatePartCatalogPdfZip(
        oem,
        master.masterAssemblyId,
        pdfLanguage
      );
      const objectUrl = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = result.fileName || "partcatalog.zip";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e: any) {
      window.alert(e?.message || "Failed to generate parts catalog PDF package.");
    } finally {
      setCatalogPdfLoading(false);
    }
  };

  const handlePartsTranslationPdfDownload = async (row: TranslationHistoryRow) => {
    if (!master?.masterAssemblyId) {
      window.alert("Master assembly id is missing.");
      return;
    }

    const language = (row.languageCode || "").trim().toLowerCase();
    const rowKey = `${row.id}-${language}`;
    setTranslationPdfLoadingKey(rowKey);
    try {
      const result = await generatePartCatalogPdfZip(
        oem,
        master.masterAssemblyId,
        language || undefined
      );
      const objectUrl = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = result.fileName || "partcatalog.zip";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e: any) {
      window.alert(e?.message || "Failed to generate parts catalog PDF package.");
    } finally {
      setTranslationPdfLoadingKey(null);
    }
  };

  const handleManualTranslationPdfDownload = async (row: TranslationHistoryRow) => {
    if (!translationSourceManual?.manualId) {
      window.alert("Manual id is missing.");
      return;
    }

    const baseUrl = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");
    if (!baseUrl) {
      window.alert("API base URL is not configured.");
      return;
    }

    const language = (row.languageCode || "").trim().toLowerCase();
    const rowKey = `${row.id}-${language}`;
    setTranslationPdfLoadingKey(rowKey);

    try {
      const query = language ? `?lang=${encodeURIComponent(language)}` : "";
      const endpoint = `${baseUrl}/PdfTest/manual/${encodeURIComponent(
        oem
      )}/${translationSourceManual.manualId}${query}`;
      const headers: HeadersInit = {};
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }

      const response = await fetch(endpoint, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        let errorMessage = `Failed to generate PDF (${response.status}).`;
        try {
          const body = await response.json();
          if (body?.message) {
            errorMessage = body.message;
          }
        } catch {
          // ignore JSON parse error
        }
        throw new Error(errorMessage);
      }

      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const match = disposition.match(/filename\*?=(?:UTF-8''|\"?)([^\";]+)/i);
      const fallbackName = `manual-${translationSourceManual.manualId}${
        language ? `-${language}` : ""
      }.pdf`;
      const filename = match
        ? decodeURIComponent(match[1].replace(/\"/g, ""))
        : fallbackName;

      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e: any) {
      window.alert(e?.message || "Failed to generate manual PDF.");
    } finally {
      setTranslationPdfLoadingKey(null);
    }
  };

  const mapManualTranslations = (
    rows: ManualTranslationLatestDto[]
  ): TranslationHistoryRow[] =>
    rows.map((row) => ({
      id: row.id,
      languageCode: row.languageCode,
      latestQueueId: row.latestQueueId ?? null,
      latestQueueStatus: row.latestQueueStatus ?? row.status ?? null,
      latestQueueCreatedOn: row.latestQueueCreatedOn ?? row.createdOn ?? null,
      latestQueueErrorMessage: row.latestQueueErrorMessage ?? null,
      latestCompletedOn: row.latestCompletedOn ?? row.updatedOn ?? row.createdOn ?? null,
    }));

  const mapPartsTranslations = (
    rows: PartsTranslationLatestDto[]
  ): TranslationHistoryRow[] =>
    rows.map((row) => ({
      id: row.id,
      languageCode: row.languageCode,
      latestQueueId: row.latestQueueId ?? null,
      latestQueueStatus: row.latestQueueStatus ?? null,
      latestQueueCreatedOn: row.latestQueueCreatedOn ?? row.createdOn ?? null,
      latestQueueErrorMessage: row.latestQueueErrorMessage ?? null,
      latestCompletedOn: row.latestCompletedOn ?? row.createdOn ?? null,
    }));

  const loadManualTranslationRows = async (targetManualId: number) => {
    setTranslationLoading(true);
    setTranslationError(null);
    setTranslationRows([]);

    try {
      const rows = await fetchLatestManualTranslationsByManual(oem, targetManualId);
      setTranslationRows(mapManualTranslations(rows));
    } catch (e: any) {
      setTranslationError(e?.message || "Failed to load translated manuals.");
    } finally {
      setTranslationLoading(false);
    }
  };

  const loadPartsTranslationRows = async (targetMasterAssemblyId: number) => {
    setTranslationLoading(true);
    setTranslationError(null);
    setTranslationRows([]);

    try {
      const rows = await fetchLatestPartsTranslationsByMasterAssembly(
        oem,
        targetMasterAssemblyId
      );
      setTranslationRows(mapPartsTranslations(rows));
    } catch (e: any) {
      setTranslationError(e?.message || "Failed to load translated parts.");
    } finally {
      setTranslationLoading(false);
    }
  };

  const openManualTranslationPopup = (manual: ManualDto) => {
    setTranslationScope("manual");
    setTranslationSourceManual(manual);
    setSelectedTranslationLanguage("");
    setShowTranslationModal(true);
    void loadManualTranslationRows(manual.manualId);
  };

  const openPartsTranslationPopup = () => {
    if (!master?.masterAssemblyId) {
      window.alert("Master assembly id is missing.");
      return;
    }
    setTranslationScope("parts");
    setTranslationSourceManual(null);
    setSelectedTranslationLanguage("");
    setShowTranslationModal(true);
    void loadPartsTranslationRows(master.masterAssemblyId);
  };

  const buildComponentDetailRoute = (
    targetMasterAssemblyId: number,
    options?: { figId?: number; lang?: string | null }
  ) => {
    const params = new URLSearchParams();
    const normalizedLang = String(options?.lang || "")
      .trim()
      .toLowerCase();
    if (normalizedLang) {
      params.set("lang", normalizedLang);
    }
    if (
      typeof options?.figId === "number" &&
      Number.isFinite(options.figId) &&
      options.figId > 0
    ) {
      params.set("figId", String(options.figId));
    }
    const query = params.toString();
    return `/component-detail/${oem}/${targetMasterAssemblyId}${
      query ? `?${query}` : ""
    }`;
  };

  const handleTranslationView = (row: TranslationHistoryRow) => {
    if (translationScope === "manual") {
      if (!master || !translationSourceManual) {
        window.alert("Manual context is missing.");
        return;
      }

      const language = (row.languageCode || "").trim().toLowerCase();
      const params = new URLSearchParams();
      params.set("masterAssemblyId", String(master.masterAssemblyId));
      params.set("translationId", String(row.id));
      if (language) {
        params.set("translationLang", language);
      }

      setPartNumber(master.componentPartNumber || "");
      setMasterAssembly(master);
      setManualTitle(
        language
          ? `${translationSourceManual.title || "Manual"} (${language.toUpperCase()})`
          : translationSourceManual.title || "Manual"
      );

      setShowTranslationModal(false);
      navigate(
        `/authoring/manuals/vendor/${translationSourceManual.manualId}?${params.toString()}`
      );
      return;
    }

    if (!master?.masterAssemblyId) {
      window.alert("Master assembly id is missing.");
      return;
    }
    const language = (row.languageCode || "").trim().toLowerCase();
    setSelectedCatalogLanguage(language);
    setShowTranslationModal(false);
    navigate(
      buildComponentDetailRoute(master.masterAssemblyId, { lang: language })
    );
  };

  const handleAddTranslation = async () => {
    const languageCode = selectedTranslationLanguage.trim().toLowerCase();
    if (!languageCode) {
      setTranslationError("Please select a language.");
      return;
    }

    setTranslationSubmitting(true);
    setTranslationError(null);
    try {
      if (translationScope === "manual") {
        if (!translationSourceManual?.manualId) {
          throw new Error("Manual context is missing.");
        }
        const content = await fetchManualJson(oem, translationSourceManual.manualId);
        if (!content?.contentId) {
          throw new Error("Manual content id is missing.");
        }

        await createManualTranslationQueue(oem, {
          contentId: content.contentId,
          elementId: null,
          elementType: null,
          languageCode,
          vendor: DEFAULT_MANUAL_TRANSLATION_VENDOR,
          priority: 0,
          status: "pending",
        });

        await loadManualTranslationRows(translationSourceManual.manualId);
      } else {
        if (!master?.masterAssemblyId) {
          throw new Error("Master assembly id is missing.");
        }

        await createPartsTranslationQueue(oem, {
          masterAssemblyId: master.masterAssemblyId,
          languageCode,
          vendor: DEFAULT_PARTS_TRANSLATION_VENDOR,
        });

        await loadPartsTranslationRows(master.masterAssemblyId);
      }

      setSelectedTranslationLanguage("");
    } catch (e: any) {
      setTranslationError(e?.message || "Failed to add translation queue item.");
    } finally {
      setTranslationSubmitting(false);
    }
  };

  const visibleManuals = useMemo(
    () =>
      manuals.filter(
        (m) =>
          ROLES_ALL.has(role) &&
          isManualVisibleForRole(role, m.status) &&
          (() => {
            const perms = getManualActionPermissions(role, m.status);
            return perms.canEdit || perms.canAuthor || perms.canView || perms.canReview;
          })()
      ),
    [manuals, role]
  );

  return (
    <div className="view-partnumber">
      <div className="view-partnumber__topbar">
        <button className="btn-outline" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>

      {loading && <div className="view-partnumber__loading">Loading…</div>}
      {error && <div className="view-partnumber__error">{error}</div>}

      {!loading && !error && master && (
        <div className="view-partnumber__layout">
          <div className="view-partnumber__left">
            <div className="view-card">
              <div className="view-card__image">
                {previewUrl ? (
                  <img src={previewUrl} alt="Component" />
                ) : (
                  <div className="view-card__image--empty">No image</div>
                )}
              </div>
              <div className="view-card__body">
                <div className="view-card__pill">{master.componentPartNumber}</div>
                <h3>{master.componentDescription}</h3>
                <p className="view-card__desc">{master.componentDescription}</p>
                <div className="view-card__meta">
                  <div>
                    <span>Last Updated</span>
                    <strong>{formatDate(master.modifiedOn || master.createdOn)}</strong>
                  </div>
                  <div>
                    <span>Revision</span>
                    <strong>{master.currentVersionId ?? "-"}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="view-partnumber__right">
            <div className="view-card">
              <div className="view-card__header">
                <div className="view-card__title">
                  <h4>Parts Catalog</h4>
                  <span className="view-card__count">{catalogCount} ITEM</span>
                </div>
                <div className="view-card__actions">
                  <span className="view-card__status">Status: {currentMasterStatusLabel}</span>
                  <label className="view-card__language">
                    <span>Language</span>
                    <select
                      value={selectedCatalogLanguage}
                      onChange={(event) =>
                        setSelectedCatalogLanguage(event.target.value)
                      }
                      disabled={loading}
                    >
                      {catalogLanguageOptions.map((option) => (
                        <option key={option.code || "en"} value={option.code}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  {canManageCatalogTranslations && (
                    <button
                      className="btn-outline"
                      type="button"
                      onClick={openPartsTranslationPopup}
                    >
                      Translation
                    </button>
                  )}
                  {canEditCatalog && (
                    isCatalogLocked ? (
                      <button
                        className="btn-primary"
                        onClick={handleUpVersion}
                        disabled={upReviseLoading}
                      >
                        {upReviseLoading ? "Up Version..." : "Up Version"}
                      </button>
                    ) : (
                      <button
                        className="btn-primary"
                        onClick={() => navigate(`/authoring/catalog/${master.masterAssemblyId}`)}
                      >
                        {catalogCount === 0 ? "Create Catalog" : "Edit Catalog"}
                      </button>
                    )
                  )}
                  <input className="view-card__filter" placeholder="Filter parts catalog..." />
                  {catalogCount > 0 && (
                    <button
                      className="btn-outline"
                      onClick={handleDownloadCatalogPdf}
                      disabled={catalogPdfLoading}
                    >
                      {catalogPdfLoading ? "Preparing PDF..." : "PDF"}
                    </button>
                  )}
                </div>
              </div>

              <div className="view-table view-table--catalog">
              <div className="view-table__head">
                <div>Actions</div>
                <div>Fig No</div>
                <div>Title</div>
                <div>Parts</div>
                <div>Ver</div>
                <div>Date</div>
              </div>
              {catalogCount > 0 ? (
                pagedCatalog.map((fig) => (
                  <div className="view-table__row" key={fig.figId}>
                    <div className="view-table__actions">
                      <button
                        className="btn-outline small"
                        onClick={() =>
                          navigate(
                            buildComponentDetailRoute(master.masterAssemblyId, {
                              figId: fig.figId,
                            })
                          )
                        }
                      >
                        View
                      </button>
                    </div>
                    <div>{fig.figNumber || "-"}</div>
                    <div>{fig.figTitle || "-"}</div>
                    <div>{fig.bomJson?.length ?? 0}</div>
                    <div>{master.currentVersionId ?? "-"}</div>
                    <div>{formatDate(master.createdOn)}</div>
                  </div>
                ))
              ) : (
                <div className="view-table__empty">No catalog entries.</div>
              )}
              {catalogCount > pageSize && (
                <div className="view-table__pagination">
                  <button
                    className="btn-outline small"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Prev
                  </button>
                  <span>
                    Page {page} of {totalPages}
                  </span>
                  <button
                    className="btn-outline small"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
              </div>
            </div>

            {SHOW_MANUALS && (
              <div className="view-card">
                <div className="view-card__header">
                  <div className="view-card__title">
                    <h4>Manuals</h4>
                    <span className="view-card__count">{visibleManuals.length} ITEMS</span>
                  </div>
                  <div className="view-card__actions">
                    {MANUAL_EDIT_ROLES.has(role) && (
                      <button
                        className="btn-primary"
                        onClick={() => navigate(`/authoring/manuals/create/${master.masterAssemblyId}`)}
                      >
                        Create New
                      </button>
                    )}
                    <input className="view-card__filter" placeholder="Filter manuals..." />
                  </div>
                </div>

                <div className="view-table view-table--manuals">
                  <div className="view-table__head">
                    <div>Actions</div>
                    <div>Manual Type</div>
                    <div>Title</div>
                    <div>Ver</div>
                    <div>Status</div>
                    <div>Created By</div>
                    <div>Date</div>
                  </div>
                  {visibleManuals.length > 0 ? (
                    visibleManuals.map((m) => {
                      const perms = getManualActionPermissions(role, m.status);
                      return (
                        <div className="view-table__row" key={m.manualId}>
                          <div className="view-table__actions">
                            {perms.canEdit && (
                              <button
                                className="btn-primary small"
                                onClick={() =>
                                  navigate(
                                    `/authoring/manuals/edit/${m.manualId}?masterAssemblyId=${master.masterAssemblyId}`,
                                    { state: { manual: m, masterAssemblyId: master.masterAssemblyId } }
                                  )
                                }
                              >
                                Edit
                              </button>
                            )}
                            {perms.canAuthor && (
                              <button
                                className="btn-outline small"
                                onClick={() => {
                                  setPartNumber(master?.componentPartNumber || "");
                                  if (master) {
                                    setMasterAssembly(master);
                                  }
                                  setManualTitle(m.title || "");
                                  navigate(`/authoring/manuals/structure/${m.manualId}`);
                                }}
                              >
                                Author
                              </button>
                            )}
                            {perms.canView && (
                              <button
                                className="btn-outline small"
                                onClick={() => {
                                  setPartNumber(master?.componentPartNumber || "");
                                  if (master) {
                                    setMasterAssembly(master);
                                  }
                                  setManualTitle(m.title || "");
                                  navigate(
                                    `/authoring/manuals/vendor/${m.manualId}?masterAssemblyId=${master.masterAssemblyId}`
                                  );
                                }}
                              >
                                View
                              </button>
                            )}
                            {perms.canReview && (
                              <button
                                className="btn-outline small"
                                onClick={() => {
                                  setPartNumber(master?.componentPartNumber || "");
                                  if (master) {
                                    setMasterAssembly(master);
                                  }
                                  setManualTitle(m.title || "");
                                  navigate(
                                    `/authoring/manuals/reviewer/${m.manualId}?masterAssemblyId=${master.masterAssemblyId}`
                                  );
                                }}
                              >
                                Review
                              </button>
                            )}
                            <button
                              className="btn-outline small"
                              onClick={() => openManualTranslationPopup(m)}
                            >
                              Translation
                            </button>
                          </div>
                          <div>{m.manualType}</div>
                          <div>{m.title}</div>
                          <div>{m.version}</div>
                          <div>{m.status}</div>
                          <div>{m.createdBy}</div>
                          <div>{formatDate(m.createdOn)}</div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="view-table__empty">No manuals available for this role/status.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showTranslationModal && (
        <div
          className="translation-modal-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowTranslationModal(false)}
        >
          <div
            className="translation-maintenance-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="translation-maintenance-modal__head">
              <div className="translation-maintenance-modal__head-copy">
                <h5 className="translation-maintenance-modal__title">
                  Translation Maintenance
                </h5>
                <p className="translation-maintenance-modal__subtitle">
                  {translationScope === "manual"
                    ? translationSourceManual
                      ? `${translationSourceManual.manualType} - ${translationSourceManual.title}`
                      : "Manual translation"
                    : master?.componentPartNumber
                    ? `Parts Catalog - ${master.componentPartNumber}`
                    : "Parts translation"}
                </p>
              </div>
              <button
                className="translation-maintenance-modal__close"
                onClick={() => setShowTranslationModal(false)}
                type="button"
              >
                ×
              </button>
            </div>
            <div className="translation-maintenance-modal__body">
              <div className="translation-maintenance-card">
                <p className="translation-maintenance-card__title">
                  ADD NEW TRANSLATION
                </p>
                <div className="translation-maintenance-card__form">
                  <div className="translation-maintenance-field">
                    <label htmlFor="translation-language">Language</label>
                    <select
                      id="translation-language"
                      value={selectedTranslationLanguage}
                      onChange={(e) =>
                        setSelectedTranslationLanguage(e.target.value)
                      }
                      disabled={translationSubmitting}
                    >
                      <option value="">Select Language</option>
                      {TRANSLATION_LANGUAGE_OPTIONS.map((option) => (
                        <option key={option.code} value={option.code}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="translation-maintenance-field">
                    <label>Date</label>
                    <div className="translation-maintenance-date">
                      {formatDate(new Date().toISOString())}
                    </div>
                  </div>

                  <div className="translation-maintenance-card__actions">
                    <button
                      className="translation-maintenance-btn translation-maintenance-btn--outline"
                      type="button"
                      onClick={() => setShowTranslationModal(false)}
                      disabled={translationSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      className="translation-maintenance-btn translation-maintenance-btn--primary"
                      type="button"
                      onClick={handleAddTranslation}
                      disabled={translationSubmitting}
                    >
                      {translationSubmitting ? "Adding..." : "Add"}
                    </button>
                  </div>
                </div>
              </div>

              {translationError ? (
                <div className="translation-maintenance-state translation-maintenance-state--error">
                  {translationError}
                </div>
              ) : null}

              <div className="translation-maintenance-history">
                <p className="translation-maintenance-history__title">
                  TRANSLATION HISTORY
                </p>

                {translationLoading ? (
                  <div className="translation-maintenance-state">
                    Loading translation history...
                  </div>
                ) : null}

                {!translationLoading && translationRows.length === 0 ? (
                  <div className="translation-maintenance-state">
                    No translation history found.
                  </div>
                ) : null}

                {!translationLoading && translationRows.length > 0 ? (
                  <div className="translation-maintenance-table-wrap">
                    <div className="translation-maintenance-table translation-maintenance-table--head">
                      <div>SERIAL NUMBER</div>
                      <div>DATE</div>
                      <div>LANGUAGE</div>
                      <div>STATUS</div>
                      <div>ACTION</div>
                    </div>
                    {translationRows.map((row) => {
                      const status = normalizeTranslationStatus(
                        row.latestQueueStatus
                      );
                      const statusClass = getTranslationStatusClass(status);
                      const languageLabel = getTranslationLanguageLabel(
                        row.languageCode
                      );
                      const statusTitle =
                        status === "FAILED" && row.latestQueueErrorMessage
                          ? row.latestQueueErrorMessage
                          : undefined;

                      return (
                        <div
                          key={`${translationScope}-${row.id}-${row.languageCode}`}
                          className="translation-maintenance-table translation-maintenance-table--row"
                        >
                          <div>{formatTranslationSerial(row.latestQueueId ?? row.id)}</div>
                          <div>
                            {formatDate(
                              row.latestQueueCreatedOn ?? row.latestCompletedOn
                            )}
                          </div>
                          <div>{languageLabel}</div>
                          <div>
                            <span
                              className={`translation-maintenance-status ${statusClass}`}
                              title={statusTitle}
                            >
                              {toStatusLabel(status)}
                            </span>
                          </div>
                          <div className="translation-maintenance-table__action">
                            {translationScope === "parts" ? (
                              <button
                                className="translation-maintenance-btn translation-maintenance-btn--outline translation-maintenance-btn--small"
                                type="button"
                                onClick={() => handlePartsTranslationPdfDownload(row)}
                                disabled={
                                  translationPdfLoadingKey ===
                                  `${row.id}-${(row.languageCode || "")
                                    .trim()
                                    .toLowerCase()}`
                                }
                              >
                                {translationPdfLoadingKey ===
                                `${row.id}-${(row.languageCode || "")
                                  .trim()
                                  .toLowerCase()}`
                                  ? "PDF..."
                                  : "PDF"}
                              </button>
                            ) : (
                              <button
                                className="translation-maintenance-btn translation-maintenance-btn--outline translation-maintenance-btn--small"
                                type="button"
                                onClick={() => handleManualTranslationPdfDownload(row)}
                                disabled={
                                  translationPdfLoadingKey ===
                                  `${row.id}-${(row.languageCode || "")
                                    .trim()
                                    .toLowerCase()}`
                                }
                              >
                                {translationPdfLoadingKey ===
                                `${row.id}-${(row.languageCode || "")
                                  .trim()
                                  .toLowerCase()}`
                                  ? "PDF..."
                                  : "PDF"}
                              </button>
                            )}
                            <button
                              className="translation-maintenance-btn translation-maintenance-btn--outline translation-maintenance-btn--small"
                              type="button"
                              onClick={() => handleTranslationView(row)}
                            >
                              View
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("en-US", { month: "short" });
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
};

type TranslationStatus =
  | "PENDING"
  | "PROCESSING"
  | "COMPLETED"
  | "FAILED"
  | "UNKNOWN";

const normalizeTranslationStatus = (
  value: string | null | undefined
): TranslationStatus => {
  const key = String(value || "").trim().toUpperCase();
  if (key === "PENDING") return "PENDING";
  if (key === "PROCESSING" || key === "IN_PROGRESS") return "PROCESSING";
  if (key === "COMPLETED" || key === "SUCCESS") return "COMPLETED";
  if (key === "FAILED" || key === "ERROR") return "FAILED";
  return "UNKNOWN";
};

const toStatusLabel = (status: TranslationStatus) => {
  if (status === "PENDING") return "Pending";
  if (status === "PROCESSING") return "Processing";
  if (status === "COMPLETED") return "Completed";
  if (status === "FAILED") return "Failed";
  return "Unknown";
};

const getTranslationStatusClass = (status: TranslationStatus) => {
  if (status === "PENDING") return "translation-maintenance-status--pending";
  if (status === "PROCESSING") return "translation-maintenance-status--processing";
  if (status === "COMPLETED") return "translation-maintenance-status--completed";
  if (status === "FAILED") return "translation-maintenance-status--failed";
  return "translation-maintenance-status--unknown";
};

const formatTranslationSerial = (value: number | null | undefined) => {
  if (!value || Number.isNaN(value)) return "-";
  return `TRN-${String(value).padStart(3, "0")}`;
};

const getTranslationLanguageLabel = (code: string | null | undefined) => {
  const key = String(code || "").trim().toLowerCase();
  if (!key) return "-";
  const match = TRANSLATION_LANGUAGE_OPTIONS.find((opt) => opt.code === key);
  return match ? match.label : key.toUpperCase();
};

export default ViewPartnumber;
